import { cloneDeep } from '@apollo/client/utilities';
import {
    Assignment,
    AutoFixHigh,
    Business,
    Description,
    Euro,
    Groups,
    LocationOn,
    Percent,
    Person,
    Schedule,
} from '@mui/icons-material';
import { Box, FormControl, IconButton, MenuItem, Paper, Select, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    EOpportunityRequestTypes,
    type IOpportunityNew,
    type TOpportunityRequestingState,
} from '@sharedInterfaces/IOpportunity';
import { checkPermissions } from '@sharedInterfaces/IPermissions';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import CertificatesInput from '@src/App/Components/General/FormularElements/formsControls/inputs/CertificatesInput/CertificatesInput';
import CompetencesInput from '@src/App/Components/General/FormularElements/formsControls/inputs/CompetencesInput/CompetencesInput';
import CurrencyInput from '@src/App/Components/General/FormularElements/formsControls/inputs/CurrencyInput/CurrencyInput';
import DateInput from '@src/App/Components/General/FormularElements/formsControls/inputs/DateInput/DateInput';
import FormatedTextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/FormatedTextInput/FormatedTextInput';
import LinkInput from '@src/App/Components/General/FormularElements/formsControls/inputs/LinkInput/LinkInput';
import NumberInput from '@src/App/Components/General/FormularElements/formsControls/inputs/NumberInput/NumberInput';
import PercentInput from '@src/App/Components/General/FormularElements/formsControls/inputs/PercentInput/PercentInput';
import RoleSelect from '@src/App/Components/General/FormularElements/formsControls/inputs/RoleSelect/RoleSelect';
import RolesInput from '@src/App/Components/General/FormularElements/formsControls/inputs/RolesInput/RolesInput';
import SkillsInput from '@src/App/Components/General/FormularElements/formsControls/inputs/SkillsInput/SkillsInput';
import TextInput from '@src/App/Components/General/FormularElements/formsControls/inputs/TextInput/TextInput';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface MvpOpportunityEditFormProps {
    opportunity: IOpportunityNew;
    requestingState: TOpportunityRequestingState;
    requestAnalyse: (type: EOpportunityRequestTypes | EOpportunityRequestTypes[]) => void;
    setOpportunity: (op: (prev: IOpportunityNew) => IOpportunityNew) => void;
}

/**
 * Edit form for opportunity editing in dialog - based on MvpOpportunityFormStep but without suggested employees
 */
function MvpOpportunityEditForm({
    opportunity,
    requestingState,
    setOpportunity,
    requestAnalyse,
}: MvpOpportunityEditFormProps) {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).opportunities;
    const allSkills = useSelector((state: AppState) => state.company.allSkills);
    const allCertificates = useSelector((state: AppState) => state.company.allCertificates);
    const allRoles = useSelector((state: AppState) => state.company.allRoles);
    const permissions = useSelector((state: AppState) => state.permissions);
    const readRoles = checkPermissions('Roles', 'Retrieve', permissions);

    const handleChange = (field: string, value: unknown) => {
        setOpportunity((prevOpportunity) => ({
            ...prevOpportunity,
            [field]: value,
        }));
    };

    const aiButtonSx = {
        minWidth: 48,
        maxWidth: 48,
        minHeight: 48,
        maxHeight: 48,
        alignSelf: 'flex-start',
    };

    return (
        <Stack spacing={3} sx={{ p: 3 }}>
            <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Grunddaten
                    </Typography>
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <Assignment fontSize="small" />
                            Titel für Anfrage:
                        </Typography>
                        <TextInput
                            size="medium"
                            value={opportunity.title}
                            onChange={(title) => handleChange('title', title)}
                            disabled={requestingState.title}
                            helperText={requestingState.title ? langStrings.oftenUsed.generating : undefined}
                            sx={{ width: '100%' }}
                        />
                    </Box>
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <Description fontSize="small" />
                            Kurzbeschreibung:
                        </Typography>
                        <TextInput
                            size="medium"
                            value={(opportunity as any).shortDescription || ''}
                            onChange={(shortDescription) => handleChange('shortDescription', shortDescription)}
                            disabled={requestingState.title}
                            helperText={requestingState.title ? langStrings.oftenUsed.generating : undefined}
                            sx={{ width: '100%' }}
                            maxLength={200}
                            rows={2}
                        />
                    </Box>
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <Description fontSize="small" />
                            Externe ID:
                        </Typography>
                        <TextInput
                            size="small"
                            value={opportunity.externalId}
                            onChange={(externalId) => handleChange('externalId', externalId)}
                            disabled={requestingState.externalId}
                            helperText={requestingState.externalId ? langStrings.oftenUsed.generating : undefined}
                            sx={{ width: '30%' }}
                        />
                    </Box>
                    <Box sx={{ width: '100%' }}>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <Assignment fontSize="small" />
                            Status:
                        </Typography>
                        <FormControl size="small" sx={{ width: '30%' }}>
                            <Select
                                value={opportunity.status || ''}
                                onChange={(e) => handleChange('status', e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="">Bitte wählen</MenuItem>
                                {useSelector((state: AppState) => state.company.opportunitySettings.statusOptions).map(
                                    (option) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.title}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                        </FormControl>
                    </Box>
                </Stack>
                <Box sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center' }}>
                    <IconButton
                        disabled={requestingState.title}
                        sx={aiButtonSx}
                        onClick={requestAnalyse.bind(null, [
                            EOpportunityRequestTypes.title,
                            EOpportunityRequestTypes.externalId,
                        ])}
                    >
                        <AutoFixHigh />
                    </IconButton>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Zeitplanung
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Schedule fontSize="small" />
                                {langStrings.startDate}:
                            </Typography>
                            <DateInput
                                value={opportunity.projectStart}
                                disabled={requestingState.projectDates}
                                helperText={requestingState.projectDates ? langStrings.oftenUsed.generating : undefined}
                                onChange={(projectStart) => handleChange('projectStart', projectStart)}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Schedule fontSize="small" />
                                {langStrings.endDate}:
                            </Typography>
                            <DateInput
                                value={opportunity.projectEnd}
                                disabled={requestingState.projectDates}
                                helperText={requestingState.projectDates ? langStrings.oftenUsed.generating : undefined}
                                onChange={(projectEnd) => handleChange('projectEnd', projectEnd)}
                            />
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Schedule fontSize="small" />
                                {langStrings.publishDate}:
                            </Typography>
                            <DateInput
                                value={opportunity.publishDate}
                                disabled={requestingState.projectDates}
                                helperText={requestingState.projectDates ? langStrings.oftenUsed.generating : undefined}
                                onChange={(publishDate) => handleChange('publishDate', publishDate)}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Schedule fontSize="small" />
                                {langStrings.deadline}:
                            </Typography>
                            <DateInput
                                value={opportunity.deadline}
                                disabled={requestingState.projectDates}
                                helperText={requestingState.projectDates ? langStrings.oftenUsed.generating : undefined}
                                onChange={(deadline) => handleChange('deadline', deadline)}
                            />
                        </Box>
                    </Stack>
                </Stack>
                <IconButton
                    disabled={requestingState.projectDates}
                    sx={aiButtonSx}
                    onClick={requestAnalyse.bind(null, EOpportunityRequestTypes.projectDates)}
                >
                    <AutoFixHigh />
                </IconButton>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Projektdetails
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ width: '50%' }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Percent fontSize="small" />
                                {langStrings.utilization}:
                            </Typography>
                            <PercentInput
                                value={opportunity.utilization}
                                disabled={requestingState.projectNumbers}
                                helperText={
                                    requestingState.projectNumbers ? langStrings.oftenUsed.generating : undefined
                                }
                                onChange={(utilization) => handleChange('utilization', utilization)}
                                sx={{ width: '30%' }}
                            />
                        </Box>
                        <Box sx={{ width: '50%' }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Percent fontSize="small" />
                                {langStrings.remote}:
                            </Typography>
                            <PercentInput
                                value={opportunity.remote}
                                disabled={requestingState.projectNumbers}
                                helperText={
                                    requestingState.projectNumbers ? langStrings.oftenUsed.generating : undefined
                                }
                                onChange={(remote) => handleChange('remote', remote)}
                                sx={{ width: '30%' }}
                            />
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ width: '50%' }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Groups fontSize="small" />
                                {langStrings.employees}:
                            </Typography>
                            <NumberInput
                                value={opportunity.headCount}
                                disabled={requestingState.projectNumbers}
                                helperText={
                                    requestingState.projectNumbers ? langStrings.oftenUsed.generating : undefined
                                }
                                onChange={(headCount) => handleChange('headCount', headCount)}
                                sx={{ width: '30%' }}
                            />
                        </Box>
                        <Box sx={{ width: '50%' }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Euro fontSize="small" />
                                {langStrings.dayPrice}:
                            </Typography>
                            <CurrencyInput
                                value={opportunity.dayPrice}
                                disabled={requestingState.projectNumbers}
                                helperText={
                                    requestingState.projectNumbers ? langStrings.oftenUsed.generating : undefined
                                }
                                onChange={(dayPrice) => handleChange('dayPrice', dayPrice)}
                                sx={{ width: '30%' }}
                            />
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ width: '50%' }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <LocationOn fontSize="small" />
                                {langStrings.location}:
                            </Typography>
                            <TextInput
                                size="small"
                                value={opportunity.loc}
                                disabled={requestingState.location}
                                helperText={requestingState.location ? langStrings.oftenUsed.generating : undefined}
                                onChange={(loc) => handleChange('loc', loc)}
                                sx={{ width: '30%' }}
                            />
                        </Box>
                        <Box sx={{ width: '50%' }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Assignment fontSize="small" />
                                Kategorie:
                            </Typography>
                            <FormControl size="small" sx={{ width: '50%' }}>
                                <Select
                                    value={(opportunity as any).category || ''}
                                    onChange={(e) => handleChange('category', e.target.value)}
                                    displayEmpty
                                >
                                    <MenuItem value="">Bitte wählen</MenuItem>
                                    <MenuItem value="development">Entwicklung</MenuItem>
                                    <MenuItem value="consulting">Beratung</MenuItem>
                                    <MenuItem value="design">Design</MenuItem>
                                    <MenuItem value="testing">Testing</MenuItem>
                                    <MenuItem value="management">Management</MenuItem>
                                    <MenuItem value="support">Support</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                </Stack>
                <IconButton
                    disabled={requestingState.projectNumbers}
                    sx={aiButtonSx}
                    onClick={requestAnalyse.bind(null, [
                        EOpportunityRequestTypes.projectNumbers,
                        EOpportunityRequestTypes.location,
                    ])}
                >
                    <AutoFixHigh />
                </IconButton>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Kunde & Quelle
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Business fontSize="small" />
                                {langStrings.customer}:
                            </Typography>
                            <TextInput
                                size="small"
                                value={opportunity.customer}
                                disabled={requestingState.customerPartner}
                                helperText={
                                    requestingState.customerPartner ? langStrings.oftenUsed.generating : undefined
                                }
                                onChange={(customer) => handleChange('customer', customer)}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Business fontSize="small" />
                                {langStrings.partner}:
                            </Typography>
                            <TextInput
                                size="small"
                                value={opportunity.partner}
                                disabled={requestingState.customerPartner}
                                helperText={
                                    requestingState.customerPartner ? langStrings.oftenUsed.generating : undefined
                                }
                                onChange={(partner) => handleChange('partner', partner)}
                            />
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Description fontSize="small" />
                                {langStrings.sourceName}:
                            </Typography>
                            <TextInput
                                size="small"
                                value={opportunity.sourceName}
                                disabled={requestingState.customerPartner}
                                helperText={
                                    requestingState.customerPartner ? langStrings.oftenUsed.generating : undefined
                                }
                                onChange={(sourceName) => handleChange('sourceName', sourceName)}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Description fontSize="small" />
                                {langStrings.sourceURL}:
                            </Typography>
                            <LinkInput
                                size="small"
                                value={opportunity.sourceURL}
                                disabled={requestingState.customerPartner}
                                helperText={
                                    requestingState.customerPartner ? langStrings.oftenUsed.generating : undefined
                                }
                                onChange={(sourceURL) => handleChange('sourceURL', sourceURL)}
                            />
                        </Box>
                    </Stack>
                </Stack>
                <IconButton
                    disabled={requestingState.customerPartner}
                    sx={aiButtonSx}
                    onClick={requestAnalyse.bind(null, [
                        EOpportunityRequestTypes.customerPartner,
                        EOpportunityRequestTypes.source,
                    ])}
                >
                    <AutoFixHigh />
                </IconButton>
            </Paper>

            {readRoles && (
                <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Stack spacing={2} sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                            Rollen
                        </Typography>
                        <Box>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                {getAppIcon(EIconCategory.ROLES)}
                                {langStrings.primaryRole}:
                            </Typography>
                            <RoleSelect
                                selectedRole={opportunity.primaryRole}
                                setSelectedRole={(role) => handleChange('primaryRole', role)}
                                disabled={requestingState.roles}
                                allowNew
                                helperText={requestingState.roles ? langStrings.oftenUsed.generating : undefined}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                {getAppIcon(EIconCategory.ROLES)}Weitere Rollen:
                            </Typography>
                            <RolesInput
                                allRoles={allRoles}
                                selectedRoles={opportunity.roles}
                                showMode={false}
                                size={'medium'}
                                disabled={requestingState.roles}
                                helperText={requestingState.roles ? langStrings.oftenUsed.generating : undefined}
                                onChange={(roles) => handleChange('roles', roles)}
                            />
                        </Box>
                    </Stack>
                    <IconButton
                        disabled={requestingState.roles}
                        sx={aiButtonSx}
                        onClick={requestAnalyse.bind(null, EOpportunityRequestTypes.roles)}
                    >
                        <AutoFixHigh />
                    </IconButton>
                </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Stack spacing={2} sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Anforderungen
                    </Typography>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, mb: 0.5, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            {getAppIcon(EIconCategory.COMPETENCES)}
                            {langStrings.oftenUsed.competences} hinzufügen:
                        </Typography>
                        <CompetencesInput
                            showMode={false}
                            size="medium"
                            competences={cloneDeep(opportunity.competences)}
                            disabled={requestingState.competences}
                            helperText={requestingState.competences ? langStrings.oftenUsed.generating : undefined}
                            onChange={(competences) => handleChange('competences', competences)}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, mb: 0.5, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            {getAppIcon(EIconCategory.SKILLS)}Benötigte Fähigkeiten hinzufügen:
                        </Typography>
                        <SkillsInput
                            size="medium"
                            showMode={false}
                            allowNewSkills={true}
                            skills={opportunity.skills}
                            allSkills={allSkills}
                            competences={opportunity.competences}
                            disabled={requestingState.skills}
                            helperText={requestingState.skills ? langStrings.oftenUsed.generating : undefined}
                            onChange={(skills) => handleChange('skills', skills)}
                        />
                    </Box>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, mb: 0.5, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            {getAppIcon(EIconCategory.CERTIFICATES)}
                            {langStrings.oftenUsed.certificates} hinzufügen:
                        </Typography>
                        <CertificatesInput
                            allCertificates={allCertificates}
                            selectedCertificates={opportunity.certificates}
                            showMode={false}
                            size="medium"
                            competences={opportunity.competences}
                            disabled={requestingState.certificates}
                            helperText={requestingState.certificates ? langStrings.oftenUsed.generating : undefined}
                            onChange={(certificates) =>
                                handleChange(
                                    'certificates',
                                    certificates.map((s) => ({ title: s.title, id: s.id })),
                                )
                            }
                        />
                    </Box>
                </Stack>
                <IconButton
                    disabled={requestingState.competences || requestingState.skills || requestingState.certificates}
                    sx={aiButtonSx}
                    onClick={requestAnalyse.bind(null, [
                        EOpportunityRequestTypes.competences,
                        EOpportunityRequestTypes.skills,
                        EOpportunityRequestTypes.certificates,
                    ])}
                >
                    <AutoFixHigh />
                </IconButton>
            </Paper>

            <Paper variant="outlined" sx={{ p: 3 }}>
                <Stack spacing={2}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        Anfrage
                    </Typography>
                    <Box>
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, mb: 0.5, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <Description fontSize="small" />
                            {langStrings.opportunity}:
                        </Typography>
                        <FormatedTextInput
                            isAI
                            value={opportunity.requestText}
                            onChange={(requestText) => handleChange('requestText', requestText)}
                        />
                    </Box>
                </Stack>
            </Paper>
        </Stack>
    );
}

export default MvpOpportunityEditForm;
